import React from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import useVoyagerStore from '../stores/useVoyagerStore';
import { getPersistenceNamespace } from '../stores/helpers/persistenceHelpers';
import { PHONE_QUERY, useChatSidebar } from './chatSidebarState';
import { useCloseToast } from './closeUndo';
import { useFinishedToast } from './simpleSidebarState';
import ShellToast from './ShellToast';
import {
  closeToast, dropIfStale, ownBuilds, shown as ackShown, useBuildsSummary, type HeldToast,
} from '../components/Builds/buildsSummary';
import type { Notice } from '../components/Builds/buildsApi';

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** What a build's toast says (#1625 R8); the name is the owner's own text, rendered as text. */
export function toastWords(n: Notice, open: number): { text: string; action: string } {
  const name = `“${n.name}”`;
  if (n.kind === 'questions') return { text: `${name} has ${plural(open, 'question')} for you. The build keeps going.`, action: 'Answer' };
  if (n.kind === 'ready') return { text: `${name} is ready to publish`, action: 'Review' };
  if (n.kind === 'failed') return { text: `${name} couldn’t finish`, action: 'Open' };
  return { text: `A workspace admin stopped ${name}`, action: 'Open' };
}

const fresh = (t: HeldToast, generation: number) => t.namespace === getPersistenceNamespace() && t.generation === generation;

/**
 * A build's toast (one claim, one toast, one ack — #1625 R3, R8): it waits while the thread toast or close-Undo is up,
 * acks its lease once it is on screen in a visible tab, goes after `ms`, and names the others it covers ("and k more
 * from your builds"). A toast from another account or workspace, or whose questions are all answered, is not shown.
 */
export default function BuildToastView({ ms }: { ms: number }) {
  const toast = useBuildsSummary((s) => s.toast);
  const generation = useBuildsSummary((s) => s.generation);
  const data = useBuildsSummary((s) => s.data);
  const threadToast = useFinishedToast((s) => !!s.toast);
  const undoToast = useCloseToast((s) => !!s.toast);
  const waiting = threadToast || undoToast;
  const phone = useMediaQuery(PHONE_QUERY);
  const drawerOpen = useChatSidebar((s) => s.drawerOpen);
  const live = toast && fresh(toast, generation) ? toast : null;
  const dataSeq = useBuildsSummary((s) => s.dataSeq);
  // the count is the claim's own (read when it was claimed), unless a list read sent after the claim says otherwise:
  // an older summary never overrides it (N12)
  const row = live && dataSeq > live.seq ? ownBuilds(data).find((b) => b.job_id === live.notice.job_id) : undefined;
  const open = row?.questions ? row.questions.open : live?.notice.open_questions ?? 0;
  const answered = !!live && live.notice.kind === 'questions' && open <= 0;
  // its copy must agree with the build now: "the build keeps going" is never said of one stopping or ended. A build's
  // state only moves forward, so any read that says so — older than the claim or not — is true now
  const now = live ? ownBuilds(data).find((b) => b.job_id === live.notice.job_id) : undefined;
  const ended = !!live && live.notice.kind === 'questions' && !!now && (now.stopping || (now.state !== 'running' && now.state !== 'queued'));
  const onScreen = !!live && !answered && !ended && !waiting && !(phone && drawerOpen);

  React.useEffect(() => {
    if (toast && (!fresh(toast, generation) || answered || ended)) closeToast(toast);     // dropped, never acked
  }, [toast, generation, answered, ended]);

  // on screen in a visible tab: ack now, and go after `ms`
  React.useEffect(() => {
    if (!onScreen || !live) return undefined;
    let acked = false;
    let timer: number | undefined;
    // the toast goes `ms` after it was first seen (acked), however long the tab was hidden before
    const ackIfVisible = () => {
      if (acked || document.hidden) return;
      acked = true;
      ackShown(live);
      timer = window.setTimeout(() => closeToast(live), ms);
    };
    ackIfVisible();
    document.addEventListener('visibilitychange', ackIfVisible);
    return () => { window.clearTimeout(timer); document.removeEventListener('visibilitychange', ackIfVisible); };
  }, [onScreen, live, ms]);

  // waiting behind another toast past its lease: dropped unacked, offered again by a later claim
  React.useEffect(() => {
    if (!live || onScreen) return undefined;
    const t = window.setInterval(() => dropIfStale(), 5000);
    return () => window.clearInterval(t);
  }, [live, onScreen]);

  if (!onScreen || !live) return null;
  const { text, action } = toastWords(live.notice, open);
  const go = (jobId: string | null) => {
    closeToast(live);
    if (!fresh(live, useBuildsSummary.getState().generation)) return;
    useVoyagerStore.getState().navigateToBuilds(jobId);
  };
  const actions = [{ label: action, onClick: () => go(live.notice.job_id) }];
  if (live.more > 0) actions.push({ label: 'All builds', onClick: () => go(null) });
  return <ShellToast testId="build-toast" wrap text={live.more > 0 ? `${text} · and ${live.more} more from your builds` : text}
    actions={actions} onDismiss={() => closeToast(live)} />;
}
